import { Row, Col, Grid, Tag } from 'antd'
import { User, Lock, Mail, Wallet, Trash2, Link } from 'lucide-react'
import { ProfileHeader } from '../Profile/ProfileHeader'
import { ProfileForm } from '../Profile/ProfileForm'
import { CreditUsageCard } from '../Profile/CreditUsageCard'
import { ChangePasswordForm } from './ChangePasswordForm'
import { ChangeEmailForm } from './ChangeEmailForm'
import { DeleteAccountForm } from './DeleteAccountForm'
import { useProfileContext } from '../../context/profile'
import { useAuthContext } from '../../context/auth'
import { FONT_WEIGHT, TYPOGRAPHY } from '../../styles/theme'
import {
  SectionCard,
  SectionHeader,
  SectionIcon,
  SectionTitle,
  SectionDescription,
  DangerSectionCard,
  DangerSectionIcon,
} from './styles/AccountSettingsSectionStyles'

function LinkedInIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="#0A66C2"
        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
      />
    </svg>
  )
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  )
}

const { useBreakpoint } = Grid

export function AccountSettingsSection() {
  const { profile } = useProfileContext()
  const { user } = useAuthContext()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const identityProviders = user?.identities?.map((i) => i.provider) ?? []
  const isGoogleUser = identityProviders.includes('google')
  const isLinkedInUser = identityProviders.includes('linkedin_oidc')

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={12}>
        <SectionCard>
          <SectionHeader>
            <SectionIcon>
              <User size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Profile Information</SectionTitle>
              <SectionDescription>Update your personal details</SectionDescription>
            </div>
          </SectionHeader>
          <ProfileHeader fullName={profile?.full_name} size={isMobile ? 80 : 96} />
          <ProfileForm />
        </SectionCard>
      </Col>
      <Col xs={24} lg={12}>
        <SectionCard>
          <SectionHeader>
            <SectionIcon>
              <Wallet size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Credit Usage</SectionTitle>
              <SectionDescription>Your monthly AI credit balance</SectionDescription>
            </div>
          </SectionHeader>
          <CreditUsageCard />
        </SectionCard>
      </Col>
      <Col xs={24} lg={12}>
        <SectionCard>
          <SectionHeader>
            <SectionIcon>
              <Lock size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Password</SectionTitle>
              <SectionDescription>Update your password</SectionDescription>
            </div>
          </SectionHeader>
          <ChangePasswordForm />
        </SectionCard>
      </Col>
      <Col xs={24} lg={12}>
        <SectionCard>
          <SectionHeader>
            <SectionIcon>
              <Mail size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Email Address</SectionTitle>
              <SectionDescription>Change your account email</SectionDescription>
            </div>
          </SectionHeader>
          <ChangeEmailForm />
        </SectionCard>
      </Col>
      {(isGoogleUser || isLinkedInUser) && (
        <Col xs={24} lg={12}>
          <SectionCard>
            <SectionHeader>
              <SectionIcon>
                <Link size={20} />
              </SectionIcon>
              <div>
                <SectionTitle>Connected Accounts</SectionTitle>
                <SectionDescription>Third-party accounts linked to your profile</SectionDescription>
              </div>
            </SectionHeader>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {isGoogleUser && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <GoogleIcon />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: FONT_WEIGHT.medium,
                        fontSize: TYPOGRAPHY.caption.fontSize,
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      Google
                    </div>
                    <div
                      style={{
                        fontSize: TYPOGRAPHY.caption.fontSize,
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      {user?.email}
                    </div>
                  </div>
                  <Tag color="success" style={{ margin: 0 }}>
                    Connected
                  </Tag>
                </div>
              )}
              {isLinkedInUser && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <LinkedInIcon />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: FONT_WEIGHT.medium,
                        fontSize: TYPOGRAPHY.caption.fontSize,
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      LinkedIn
                    </div>
                    <div
                      style={{
                        fontSize: TYPOGRAPHY.caption.fontSize,
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      {user?.email}
                    </div>
                  </div>
                  <Tag color="success" style={{ margin: 0 }}>
                    Connected
                  </Tag>
                </div>
              )}
            </div>
          </SectionCard>
        </Col>
      )}

      <Col xs={24}>
        <DangerSectionCard>
          <SectionHeader>
            <DangerSectionIcon>
              <Trash2 size={20} />
            </DangerSectionIcon>
            <div>
              <SectionTitle>Delete Account</SectionTitle>
              <SectionDescription>Permanently delete your account and all data</SectionDescription>
            </div>
          </SectionHeader>
          <DeleteAccountForm />
        </DangerSectionCard>
      </Col>
    </Row>
  )
}
